/**
 * SetupGuidePage.tsx
 *
 * In-admin Setup Guide for public file hosts used by GenesisBox and
 * GenesisView. Three tabs:
 *
 *   1. Public File Host — connect R2 / DreamHost public files
 *   2. View CORS        — required headers for PDF.js / GenesisView
 *   3. Verify           — paste a public file URL, we test CORS + range
 *
 * Lives at /admin/setup-guide. Linked from ConnectionDialog's
 * "Public File Base URL" field helper text.
 *
 * IMPORTANT: this page is the documentation. No external docs site
 * needed for v1 (per /plan-devex-review user direction). Customer
 * stays in-product end-to-end during R2 + CF setup.
 */

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import {
  GENESIS_VIEW_ORIGIN,
  VIEW_CORS_ALLOWED_HEADERS,
  VIEW_CORS_ALLOWED_METHODS,
  VIEW_CORS_ALLOWED_ORIGINS,
  VIEW_CORS_EXPOSED_HEADERS,
  buildCloudflareR2CorsJson,
  buildDreamHostHtaccessCorsSnippet,
  buildPublicFilesHtaccessCorsSnippet,
  buildViewCorsCurlCommand,
} from '@utils/helpers/viewCorsProtocol';
import { probeViewCorsUrl, type ViewCorsUrlProbe } from '@utils/helpers/viewerHealth';

const R2_CORS_JSON = buildCloudflareR2CorsJson();
const DREAMHOST_STRICT_HTACCESS = buildDreamHostHtaccessCorsSnippet();
const DREAMHOST_PUBLIC_HTACCESS = buildPublicFilesHtaccessCorsSnippet();

interface CodeBlockProps {
  code: string;
  language?: string;
}

function CodeBlock({ code, language = 'js' }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard rejected — fall back to selection
      setCopied(false);
    }
  }

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: 'rgba(11,11,13,0.92)',
        border: '1px solid rgba(143,245,255,0.12)',
        borderRadius: 2,
        p: 2,
        overflow: 'auto',
      }}
    >
      <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
        <IconButton
          onClick={handleCopy}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(143,245,255,0.08)',
            '&:hover': { bgcolor: 'rgba(143,245,255,0.16)' },
          }}
          aria-label="Copy code to clipboard"
        >
          {copied ? (
            <CheckCircleRoundedIcon sx={{ fontSize: 18, color: '#5EBA7D' }} />
          ) : (
            <ContentCopyRoundedIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.65)' }} />
          )}
        </IconButton>
      </Tooltip>
      <Box
        component="pre"
        sx={{
          margin: 0,
          fontFamily: '"JetBrains Mono", "Menlo", monospace',
          fontSize: 13,
          color: 'rgba(255,255,255,0.92)',
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        <code data-language={language}>{code}</code>
      </Box>
    </Box>
  );
}

interface TabPanelProps {
  index: number;
  current: number;
  children: React.ReactNode;
}

function TabPanel({ index, current, children }: TabPanelProps) {
  if (index !== current) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

type VerifyResult = ViewCorsUrlProbe | { kind: 'running' };

function VerifyTab() {
  const [domain, setDomain] = useState('');
  const [result, setResult] = useState<VerifyResult>({ kind: 'unchecked' });

  async function handleRunTest() {
    const trimmed = domain.trim();
    if (!trimmed) return;
    setResult({ kind: 'running' });
    setResult(await probeViewCorsUrl(trimmed));
  }

  const curlCommand = buildViewCorsCurlCommand(domain);

  return (
    <Stack spacing={2.5}>
      <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
        Paste a public PDF or file URL. We&apos;ll test whether this Box page can
        fetch it with CORS and whether PDF range headers are visible.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
        <TextField
          label="Public file URL"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="https://seedfiles.genesisframeworks.com/prayerfile/2025-09_Prayer.pdf"
          fullWidth
          size="small"
        />
        <Button
          variant="contained"
          onClick={() => { void handleRunTest(); }}
          disabled={result.kind === 'running'}
          sx={{
            bgcolor: '#8ff5ff',
            color: '#003f43',
            minWidth: 120,
            '&:hover': { bgcolor: '#69edf9' },
          }}
        >
          {result.kind === 'running' ? <CircularProgress size={20} /> : 'Run Test'}
        </Button>
      </Stack>

      {result.kind === 'invalid-url' && (
        <Alert severity="error" icon={<ErrorRoundedIcon />}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Enter a full HTTPS file URL.</Typography>
          <Typography variant="body2">{result.message}</Typography>
        </Alert>
      )}

      {result.kind === 'timeout' && (
        <Alert severity="error" icon={<ErrorRoundedIcon />}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Test timed out.</Typography>
          <Typography variant="body2">
            The file host didn&apos;t respond within 8 seconds.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Check DNS, the public file path, and whether the file host blocks HEAD or Range requests.
          </Typography>
        </Alert>
      )}

      {result.kind === 'cors-blocked' && (
        <Alert severity="warning" icon={<ErrorRoundedIcon />}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>CORS blocked this browser request.</Typography>
          <Typography variant="body2">
            The file may exist, but the host is not allowing this Box origin to read it.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Apply the R2 CORS JSON or DreamHost .htaccess from the View CORS tab.
          </Typography>
        </Alert>
      )}

      {result.kind === 'unreachable' && (
        <Alert severity="error" icon={<ErrorRoundedIcon />}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Couldn&apos;t reach the file.</Typography>
          <Typography variant="body2">{result.message}</Typography>
        </Alert>
      )}

      {result.kind === 'success' && (
        <Stack spacing={1.5}>
          <Alert severity={result.rangeStatus === 'ok' ? 'success' : 'warning'} icon={result.rangeStatus === 'ok' ? <CheckCircleRoundedIcon /> : <ErrorRoundedIcon />}>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
              CORS fetch works from this Box page (HTTP {result.status}).
            </Typography>
            {result.rangeStatus === 'ok' ? (
              <Typography variant="body2">
                PDF range headers are visible, so GenesisView/PDF.js should be able to stream this file.
              </Typography>
            ) : (
              <Typography variant="body2">
                The file is reachable, but PDF range support is not fully visible
                ({result.rangeStatus}). Expose these headers: {result.missingCoreHeaders.join(', ') || 'Accept-Ranges, Content-Length, Content-Range'}.
              </Typography>
            )}
            {result.visibleHeaders.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Visible headers: {result.visibleHeaders.join(', ')}
              </Typography>
            )}
          </Alert>
          <Alert severity="info" sx={{ bgcolor: 'rgba(143,245,255,0.06)' }}>
            <Typography variant="body2">
              Browser tests run from the current Box origin. To verify the exact GenesisView origin,
              run this command and check for Access-Control-Allow-Origin plus Content-Range.
            </Typography>
          </Alert>
          <CodeBlock code={curlCommand} language="bash" />
        </Stack>
      )}
    </Stack>
  );
}

export function SetupGuidePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  return (
    <Container maxWidth="md" sx={{ py: 5, color: 'rgba(255,255,255,0.92)' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Tooltip title="Back to Drive">
          <IconButton
            onClick={() => navigate('/drive')}
            aria-label="Back to Drive"
            sx={{ color: 'rgba(255,255,255,0.65)' }}
          >
            <ArrowBackRoundedIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography
            className="font-label"
            sx={{ fontSize: 10, color: '#8ff5ff', letterSpacing: '0.18em', mb: 0.5 }}
          >
            ADMIN / SETUP GUIDE
          </Typography>
          <Typography variant="h4" sx={{ fontSize: { xs: 24, md: 32 } }}>
            View CORS Protocol
          </Typography>
        </Box>
      </Stack>

      <Typography sx={{ color: 'rgba(255,255,255,0.65)', mb: 4 }}>
        Configure R2, DreamHost, or any public file host so GenesisBox can open
        PDFs through <code>view.genesisframeworks.com</code> without public CORS proxies.
      </Typography>

      <Paper
        elevation={0}
        sx={{
          bgcolor: 'rgba(11,11,13,0.78)',
          border: '1px solid rgba(143,245,255,0.10)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            borderBottom: '1px solid rgba(143,245,255,0.08)',
            '& .MuiTab-root': {
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: 12,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.55)',
            },
            '& .Mui-selected': { color: '#8ff5ff !important' },
            '& .MuiTabs-indicator': { backgroundColor: '#8ff5ff' },
          }}
        >
          <Tab label="1 · Public Files" />
          <Tab label="2 · View CORS" />
          <Tab label="3 · Verify" />
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 4 } }}>
          {/* Tab 1: Public file host */}
          <TabPanel index={0} current={tab}>
            <Stack spacing={2.5}>
              <Typography variant="h6">Use a stable public file base URL</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
                GenesisBox stores a <strong>Public File Base URL</strong> on each
                connection. PDF buttons use it to build the real file URL, then
                open GenesisView as <code>smart.html?url=...</code>.
              </Typography>

              <Divider sx={{ borderColor: 'rgba(143,245,255,0.08)' }} />

              <Stack spacing={2}>
                <Box>
                  <Typography sx={{ fontWeight: 600, mb: 0.5 }}>R2 custom domain</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)' }}>
                    In Cloudflare, open your R2 bucket, enable Public Access, and
                    connect a custom domain such as <code>seedfiles.genesisframeworks.com</code>.
                    Use that domain as the connection&apos;s Public File Base URL.
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, mb: 0.5 }}>DreamHost public folder</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)' }}>
                    If files live under a public folder, use the folder&apos;s public
                    HTTPS base URL and add the .htaccess rules from the View CORS tab.
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Connection prefix</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)' }}>
                    If your connection prefix is <code>prayerfile/</code>, keep
                    Public File Base URL at the domain root. GenesisBox combines
                    the prefix and file path when building the GenesisView URL.
                  </Typography>
                </Box>
              </Stack>

              <Box>
                <Button
                  endIcon={<OpenInNewRoundedIcon />}
                  href="https://dash.cloudflare.com/?to=/:account/r2/default/buckets"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: '#8ff5ff' }}
                >
                  Open Cloudflare R2 dashboard
                </Button>
              </Box>
            </Stack>
          </TabPanel>

          {/* Tab 2: GenesisView file CORS */}
          <TabPanel index={1} current={tab}>
            <Stack spacing={2.5}>
              <Typography variant="h6">Allow GenesisView and Box to read files</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
                When PDF.js runs on <code>{GENESIS_VIEW_ORIGIN}</code> and fetches
                a PDF from your public file host, the file host must send CORS
                headers. These are the project-level rules GenesisBox expects.
              </Typography>

              <Alert severity="info" sx={{ bgcolor: 'rgba(143,245,255,0.06)' }}>
                Required origins: {VIEW_CORS_ALLOWED_ORIGINS.join(', ')}. For
                fully public church files, <code>*</code> is also acceptable.
              </Alert>

              <Box
                sx={{
                  display: 'grid',
                  gap: 1,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                }}
              >
                <Alert severity="info" sx={{ bgcolor: 'rgba(255,255,255,0.04)' }}>
                  <Typography variant="body2">
                    Methods: {VIEW_CORS_ALLOWED_METHODS.join(', ')}
                  </Typography>
                </Alert>
                <Alert severity="info" sx={{ bgcolor: 'rgba(255,255,255,0.04)' }}>
                  <Typography variant="body2">
                    Allow headers: {VIEW_CORS_ALLOWED_HEADERS.join(', ')}
                  </Typography>
                </Alert>
                <Alert severity="info" sx={{ bgcolor: 'rgba(255,255,255,0.04)' }}>
                  <Typography variant="body2">
                    Expose: {VIEW_CORS_EXPOSED_HEADERS.join(', ')}
                  </Typography>
                </Alert>
              </Box>

              <Stack spacing={2}>
                <Box>
                  <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Cloudflare R2 CORS JSON</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)' }}>
                    R2 bucket settings can import this CORS policy. With wrangler,
                    save it as <code>cors.json</code> and run
                    <code> wrangler r2 bucket cors put BUCKET_NAME --file cors.json</code>.
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <CodeBlock code={R2_CORS_JSON} language="json" />
                  </Box>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, mb: 0.5 }}>DreamHost .htaccess, strict origins</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)' }}>
                    Put this in the public folder that serves PDFs. It allows only
                    GenesisView and GenesisBox.
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <CodeBlock code={DREAMHOST_STRICT_HTACCESS} language="apache" />
                  </Box>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, mb: 0.5 }}>DreamHost .htaccess, public files</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)' }}>
                    If the folder contains public files and no credentials are used,
                    this wildcard variant is simpler and works across Box, View, and future tools.
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <CodeBlock code={DREAMHOST_PUBLIC_HTACCESS} language="apache" />
                  </Box>
                </Box>
              </Stack>

              <Box>
                <Button
                  endIcon={<OpenInNewRoundedIcon />}
                  href="https://developers.cloudflare.com/r2/buckets/cors/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: '#8ff5ff' }}
                >
                  Open R2 CORS docs
                </Button>
              </Box>
            </Stack>
          </TabPanel>

          {/* Tab 3: Verify */}
          <TabPanel index={2} current={tab}>
            <VerifyTab />
          </TabPanel>
        </Box>
      </Paper>

      <Typography
        sx={{
          color: 'rgba(255,255,255,0.50)',
          fontSize: 12,
          mt: 3,
        }}
      >
        Need help?{' '}
        <Link
          href="https://github.com/Nayatrei/GenesisBox/issues"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: '#8ff5ff' }}
        >
          File an issue on GitHub
        </Link>
        .
      </Typography>
    </Container>
  );
}

export default SetupGuidePage;
